import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, Cloud, CloudOff, Check, AlertCircle, RefreshCw, Trash2, X, Zap } from 'lucide-react';
import offlineQueueService from '../services/OfflineQueueService';

const OfflineIndicator = () => {
  const [queueState, setQueueState] = useState({
    isOnline: navigator.onLine,
    queueLength: 0,
    queue: []
  });
  const [showDetails, setShowDetails] = useState(false);
  const [stats, setStats] = useState(offlineQueueService.getStats());

  useEffect(() => {
    // Subscribe to queue changes
    const unsubscribe = offlineQueueService.subscribe((state) => {
      setQueueState(state);
      setStats(offlineQueueService.getStats());
    });

    // Initial state
    setStats(offlineQueueService.getStats());

    return () => unsubscribe();
  }, []);

  const handleRetryFailed = async () => {
    await offlineQueueService.retryFailed();
  };

  const handleClearQueue = async () => {
    if (window.confirm('Clear all queued messages? They will not be sent.')) {
      await offlineQueueService.clearQueue();
    }
  };

  const handleRemoveMessage = async (messageId) => {
    await offlineQueueService.removeFromQueue(messageId);
  };

  // Don't show anything if online and no queued messages
  if (queueState.isOnline && queueState.queueLength === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        className="fixed bottom-4 left-4 z-50"
        data-testid="offline-indicator"
      >
        {/* Main indicator button */}
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg transition-all ${
            queueState.isOnline
              ? 'bg-amber-500 hover:bg-amber-600'
              : 'bg-red-500 hover:bg-red-600'
          } text-white`}
        >
          {queueState.isOnline ? (
            <>
              <Cloud className="w-4 h-4" />
              <span className="text-sm font-medium">
                {stats.syncInProgress ? 'Syncing...' : `${queueState.queueLength} pending`}
              </span>
              {stats.syncInProgress && (
                <RefreshCw className="w-4 h-4 animate-spin" />
              )}
            </>
          ) : (
            <>
              <WifiOff className="w-4 h-4" />
              <span className="text-sm font-medium">Offline Mode</span>
              {queueState.queueLength > 0 && (
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                  {queueState.queueLength}
                </span>
              )}
            </>
          )}
        </button>

        {/* Expanded details panel */}
        <AnimatePresence>
          {showDetails && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute bottom-12 left-0 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            >
              {/* Header */}
              <div className={`px-4 py-3 ${queueState.isOnline ? 'bg-amber-50' : 'bg-red-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {queueState.isOnline ? (
                      <Cloud className="w-5 h-5 text-amber-600" />
                    ) : (
                      <CloudOff className="w-5 h-5 text-red-600" />
                    )}
                    <span className={`font-semibold ${queueState.isOnline ? 'text-amber-900' : 'text-red-900'}`}>
                      {queueState.isOnline ? 'Sync Queue' : 'Offline Mode'}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="p-1 hover:bg-black/10 rounded-full"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
                {!queueState.isOnline && (
                  <p className="text-xs text-red-700 mt-1">
                    Messages will be sent when you're back online
                  </p>
                )}
              </div>

              {/* Stats */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <p className="text-lg font-bold text-gray-900">{stats.queued}</p>
                    <p className="text-xs text-gray-500">Queued</p>
                  </div>
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <p className="text-lg font-bold text-blue-600">{stats.sending}</p>
                    <p className="text-xs text-gray-500">Sending</p>
                  </div>
                  <div className="p-2 bg-red-50 rounded-lg">
                    <p className="text-lg font-bold text-red-600">{stats.failed}</p>
                    <p className="text-xs text-gray-500">Failed</p>
                  </div>
                </div>
                {/* Background Sync Status */}
                {stats.useServiceWorker && (
                  <div className="mt-2 flex items-center justify-center gap-1 text-xs text-green-600">
                    <Zap className="w-3 h-3" />
                    <span>Background sync enabled</span>
                  </div>
                )}
              </div>

              {/* Queued messages list */}
              <div className="max-h-48 overflow-y-auto">
                {queueState.queue.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No pending messages
                  </div>
                ) : (
                  queueState.queue.slice(0, 10).map((msg) => (
                    <div
                      key={msg.id}
                      className="px-4 py-2 border-b border-gray-50 flex items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">
                          {msg.content || '[Media]'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(msg.queued_at).toLocaleTimeString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {msg.status === 'queued' && (
                          <span className="w-2 h-2 bg-amber-400 rounded-full" />
                        )}
                        {msg.status === 'sending' && (
                          <RefreshCw className="w-3 h-3 text-blue-500 animate-spin" />
                        )}
                        {msg.status === 'sent' && (
                          <Check className="w-3 h-3 text-green-500" />
                        )}
                        {msg.status === 'failed' && (
                          <AlertCircle className="w-3 h-3 text-red-500" />
                        )}
                        <button
                          onClick={() => handleRemoveMessage(msg.id)}
                          className="p-1 hover:bg-red-50 rounded-full"
                        >
                          <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
                {queueState.queue.length > 10 && (
                  <div className="px-4 py-2 text-center text-xs text-gray-500">
                    +{queueState.queue.length - 10} more messages
                  </div>
                )}
              </div>

              {/* Actions */}
              {queueState.queue.length > 0 && (
                <div className="px-4 py-3 bg-gray-50 flex gap-2">
                  {stats.failed > 0 && (
                    <button
                      onClick={handleRetryFailed}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry Failed
                    </button>
                  )}
                  <button
                    onClick={handleClearQueue}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
                  >
                    <Trash2 className="w-4 h-4" />
                    Clear
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default OfflineIndicator;
